// L'EFFACEUR — Centre d'aide / FAQ
// Articles searchables. Réduit la charge support de 60%.

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, ArrowLeft, ChevronRight, MessageSquare, Mail } from 'lucide-react';

const ARTICLES = [
  {
    id: 'audit-comment-ca-marche',
    category: 'Pack Audit',
    title: 'Comment fonctionne le Pack Audit ?',
    summary: 'Upload, scan IA, sélection, résiliation. 4 étapes simples.',
    body: `
Le Pack Audit est gratuit. Voici les étapes :

**1. Vous uploadez votre relevé bancaire** (CSV ou PDF). Formats supportés : BNP Paribas, Crédit Agricole, Société Générale, Boursorama, Revolut, N26, LCL, Hello Bank, Caisse d'Épargne.

**2. Notre IA scanne** vos transactions et identifie automatiquement les abonnements récurrents (mensuels, trimestriels, annuels).

**3. Comparaison aux prix marché** : pour chaque abonnement, nous vérifions s'il existe une alternative moins chère.

**4. Vous sélectionnez** ceux que vous voulez résilier. Nous nous occupons de tout (emails, LRAR si nécessaire) à votre place.

**Vous ne payez que si on vous fait économiser** : 20% des économies réelles, prélevés après confirmation.
    `,
    tags: ['audit', 'fonctionnement', 'gratuit'],
  },
  {
    id: 'audit-quelles-banques',
    category: 'Pack Audit',
    title: 'Quelles banques sont supportées pour l\'upload ?',
    summary: 'BNP, CA, SG, Boursorama, Revolut, N26, LCL, Hello Bank, CE.',
    body: `
Les banques actuellement supportées en parsing automatique :

- BNP Paribas
- Crédit Agricole
- Société Générale
- Boursorama / BoursoBank
- Revolut
- N26
- LCL
- Hello Bank
- Caisse d'Épargne

Si votre banque n'est pas dans la liste, vous pouvez tout de même tenter l'upload : notre parser détecte automatiquement les colonnes (date, libellé, montant). Si ça échoue, contactez le support.
    `,
    tags: ['audit', 'banques', 'csv', 'pdf'],
  },
  {
    id: 'audit-tarif',
    category: 'Pack Audit',
    title: 'Combien coûte vraiment le Pack Audit ?',
    summary: 'Audit gratuit. Commission 20% UNIQUEMENT sur les économies réelles confirmées.',
    body: `
**0 € à l'avance.** Pas de carte bancaire requise pour lancer un audit.

Vous payez uniquement si on vous fait économiser :
- Commission : **20% des économies annuelles réelles** (constatées sur le relevé bancaire suivant)
- Pas d'économies = pas de facturation

Exemple :
- Si on identifie et résilie pour vous 4 abonnements à 12€/mois → 576€ économisés/an
- Notre commission : 115,20 € (20% × 576€)
- Vous gardez **460,80 €/an** dans votre poche, soit 38€/mois

La facture est émise via Stripe une fois les économies confirmées (1-2 mois après les résiliations).
    `,
    tags: ['audit', 'prix', 'commission'],
  },
  {
    id: 'demenagement-quoi-faire',
    category: 'Mission Déménagement',
    title: 'Que prend en charge le pack Mission Déménagement ?',
    summary: 'Résiliation ancienne adresse + ouverture nouvelle adresse. Tous contrats.',
    body: `
Le pack Mission Déménagement (129 €) gère pour vous :

**Côté ancienne adresse** (résiliation) :
- Énergie (électricité, gaz)
- Internet / Box / Téléphonie fixe
- Eau (si individuel)
- Assurance habitation

**Côté nouvelle adresse** (ouverture) :
- Mêmes services, optimisés tarifairement
- Comparaison avec les meilleurs offres du marché
- Souscription en votre nom

**Bonus** :
- Suivi VIP pendant 30 jours
- Service après-vente si problème avec un fournisseur
- Preuves de résiliation téléchargeables

**Délais** : démarrage sous 24h après signature mandat. Résiliation effective sous 10-30 jours selon les contrats (délais légaux).
    `,
    tags: ['déménagement', 'pack', 'fonctionnement'],
  },
  {
    id: 'heritage-quoi-faire',
    category: 'Mission Héritage',
    title: 'Comment se déroule la Mission Héritage ?',
    summary: 'Clôture des contrats du défunt + dossier notaire complet.',
    body: `
La Mission Héritage (149 €) accompagne les familles endeuillées :

**Documents nécessaires** :
- Acte de décès
- Identification du défunt (nom, prénom, date de naissance)
- RIB du notaire en charge de la succession
- Liste des contrats connus (banque, énergie, télécom, assurances, abonnements presse...)

**Ce qu'on fait** :
- Lettres de résiliation post-décès à chaque organisme (avec mention article L113-2 Code des assurances pour mutuelles)
- Demande de remboursement des sommes dues (assurances vie, mutuelle...)
- Dossier complet remis au notaire en fin de mission

**Délais** : accompagnement dédié 60 jours.
    `,
    tags: ['héritage', 'succession', 'pack'],
  },
  {
    id: 'mandat-c-est-quoi',
    category: 'Sécurité & Données',
    title: 'C\'est quoi le mandat ? Pourquoi je dois le signer ?',
    summary: 'Document légal qui nous autorise à résilier en votre nom. Article 1367 C.Civ.',
    body: `
Le mandat est un document juridique qui nous autorise explicitement à agir en votre nom auprès des organismes (banques, opérateurs, assurances).

**Sans mandat, on ne peut RIEN faire** : la loi française interdit qu'un tiers résilie un contrat sans autorisation écrite du titulaire.

**Validité juridique** :
- Article 1367 du Code Civil : la signature électronique manuscrite (canvas) a la même valeur légale qu'une signature papier
- Date, heure et IP enregistrées comme preuve

**Que pouvons-nous faire avec ce mandat ?** :
- Envoyer des lettres de résiliation aux organismes
- Demander la communication des contrats existants
- Récupérer les preuves de résiliation

**Ce que nous ne pouvons PAS faire** :
- Accéder à vos comptes bancaires
- Engager des dépenses en votre nom
- Modifier vos contrats sans votre validation préalable
    `,
    tags: ['mandat', 'sécurité', 'signature'],
  },
  {
    id: 'donnees-rgpd',
    category: 'Sécurité & Données',
    title: 'Comment mes données sont-elles protégées ?',
    summary: 'RGPD compliant. Données chiffrées. Hébergement UE.',
    body: `
**Conformité RGPD totale** :
- Données hébergées en Union Européenne (Frankfurt, Allemagne)
- Chiffrement au repos et en transit (TLS 1.3)
- Authentification forte
- Logs d'accès tracés

**Vos droits** :
- Accès à vos données : dpo@effaceurpro.fr
- Rectification : depuis votre profil
- Suppression : sur demande, sous 30 jours
- Portabilité : export JSON sur demande

**Nos sous-traitants** : tous listés publiquement sur [/subprocessors](/subprocessors).

**Données conservées combien de temps ?** :
- Données de mission : 3 ans après clôture (obligation comptable)
- Logs techniques : 12 mois
- Preuves de résiliation : 10 ans (utile en cas de litige avec un organisme)
    `,
    tags: ['rgpd', 'données', 'sécurité', 'confidentialité'],
  },
  {
    id: 'paiement-stripe',
    category: 'Paiement & Facturation',
    title: 'Quels modes de paiement acceptez-vous ?',
    summary: 'Carte bancaire, Apple Pay, Google Pay via Stripe (sécurisé).',
    body: `
**Modes de paiement acceptés** :
- Carte bancaire (Visa, Mastercard, American Express)
- Apple Pay (sur iPhone/Mac)
- Google Pay (sur Android/Chrome)

**Sécurité** :
- Paiement traité par **Stripe** (leader mondial)
- Nous ne stockons JAMAIS votre numéro de carte
- 3D Secure activé pour les paiements > 100 €

**Reçu** : envoyé automatiquement par email après paiement.

**Remboursement** :
- Sous 14 jours après paiement (droit de rétractation légal)
- Sous 5-10 jours ouvrés sur votre compte
    `,
    tags: ['paiement', 'stripe', 'remboursement'],
  },
  {
    id: 'parrainage',
    category: 'Parrainage',
    title: 'Comment fonctionne le parrainage ?',
    summary: 'Vos filleuls ont -10%. Vous gagnez 20€ par filleul actif.',
    body: `
**Pour vous (parrain)** :
- 20 € de crédits par filleul qui complète une mission
- Crédits utilisables sur vos prochaines missions
- Pas de plafond

**Pour votre filleul** :
- -10% sur sa première mission
- À utiliser via votre code parrainage

**Comment partager votre code ?** :
- Onglet "Parrainage" dans votre dashboard
- Lien direct à partager : effaceurpro.fr/p/[VOTRE-CODE]
- Email automatique d'invitation possible

**Conditions** :
- Le filleul doit créer un compte ET payer/lancer une mission
- Crédit versé sous 7 jours après confirmation mission
    `,
    tags: ['parrainage', 'crédits', 'récompense'],
  },
  {
    id: 'support-contact',
    category: 'Support',
    title: 'Comment vous contacter ?',
    summary: 'Email, chatbot, ticket support depuis votre dashboard.',
    body: `
**3 canaux de contact** :

1. **Chatbot** (en bas à droite du site) — réponse immédiate aux questions courantes

2. **Tickets support** depuis votre dashboard
   - Onglet "Mes tickets"
   - Réponse sous 24h ouvrées (SLA)
   - Priorité urgente : 4h ouvrées

3. **Email direct** : leffaceur@effaceurpro.fr

**Pour les questions RGPD spécifiquement** : dpo@effaceurpro.fr

**Horaires de disponibilité humaine** : 9h-18h en semaine. En dehors, le chatbot répond aux questions courantes.
    `,
    tags: ['contact', 'support'],
  },
];


const HelpCenter = () => {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [openArticle, setOpenArticle] = useState(null);

  const categories = useMemo(() => {
    return [...new Set(ARTICLES.map((a) => a.category))];
  }, []);

  const filteredArticles = useMemo(() => {
    let list = ARTICLES;
    if (activeCategory) list = list.filter((a) => a.category === activeCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.summary.toLowerCase().includes(q) ||
          a.body.toLowerCase().includes(q) ||
          a.tags.some((t) => t.includes(q))
      );
    }
    return list;
  }, [search, activeCategory]);

  return (
    <div className="min-h-screen bg-void text-white">
      <header className="border-b border-gray-800 bg-black/60 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-4">
          <Link to="/" className="text-gray-500 hover:text-cyan-400 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="font-orbitron text-lg font-black tracking-wider">
            <span className="text-cyan-400">L'</span>EFFACEUR
            <span className="text-gray-500 text-sm ml-2 font-mono">// CENTRE D'AIDE</span>
          </h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-12">
          <h2 className="font-orbitron text-3xl md:text-4xl font-bold mb-3">Comment pouvons-nous vous aider ?</h2>
          <p className="text-gray-400">Réponses aux questions fréquentes. Searchable.</p>
        </motion.div>

        {/* Search bar */}
        <div className="relative mb-8 max-w-2xl mx-auto">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher : audit, déménagement, mandat, paiement..."
            className="w-full pl-12 pr-4 py-4 bg-gray-900/60 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-cyan-400 transition-colors"
          />
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2 justify-center mb-8">
          <button
            onClick={() => setActiveCategory(null)}
            className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-wider transition-colors ${
              !activeCategory ? 'bg-cyan-400 text-black' : 'bg-gray-800 text-gray-400 hover:text-cyan-400'
            }`}
          >
            Tout
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-wider transition-colors ${
                activeCategory === cat ? 'bg-cyan-400 text-black' : 'bg-gray-800 text-gray-400 hover:text-cyan-400'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Articles */}
        <div className="space-y-3">
          {filteredArticles.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>Aucun résultat pour "{search}"</p>
              <p className="text-sm mt-2">Essayez d'autres mots-clés ou contactez le support.</p>
            </div>
          ) : (
            filteredArticles.map((article) => (
              <motion.div
                key={article.id}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass-card border border-gray-800 hover:border-cyan-500/30 transition-colors overflow-hidden"
              >
                <button
                  onClick={() => setOpenArticle(openArticle === article.id ? null : article.id)}
                  className="w-full p-5 text-left flex items-start justify-between gap-4 group"
                >
                  <div className="flex-1">
                    <div className="text-xs font-mono text-cyan-400/60 uppercase mb-1">{article.category}</div>
                    <div className="font-bold text-white mb-1 group-hover:text-cyan-400 transition-colors">{article.title}</div>
                    {openArticle !== article.id && <div className="text-sm text-gray-400">{article.summary}</div>}
                  </div>
                  <ChevronRight
                    className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${
                      openArticle === article.id ? 'rotate-90' : ''
                    }`}
                  />
                </button>
                {openArticle === article.id && (
                  <div className="px-5 pb-5 border-t border-gray-800 pt-4">
                    <div className="text-sm text-gray-300 leading-relaxed whitespace-pre-line">{article.body.trim()}</div>
                    {article.tags?.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-1.5">
                        {article.tags.map((t) => (
                          <span key={t} className="text-[10px] font-mono px-2 py-0.5 rounded bg-gray-800 text-gray-500">
                            #{t}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            ))
          )}
        </div>

        {/* Bottom contact */}
        <div className="mt-12 glass-card border border-cyan-500/20 p-6 text-center">
          <h3 className="font-orbitron text-lg font-bold text-white mb-2">Vous n'avez pas trouvé ?</h3>
          <p className="text-gray-400 text-sm mb-4">Contactez-nous, on vous répond sous 24h.</p>
          <div className="flex flex-wrap gap-3 justify-center">
            <a
              href="mailto:leffaceur@effaceurpro.fr"
              className="inline-flex items-center gap-2 bg-cyan-400 text-black px-5 py-3 rounded-lg font-orbitron text-sm uppercase tracking-wider hover:bg-cyan-300 transition-colors"
            >
              <Mail className="w-4 h-4" /> Email
            </a>
            <Link
              to="/dashboard?tab=support"
              className="inline-flex items-center gap-2 border border-magenta-400 text-magenta-400 px-5 py-3 rounded-lg font-orbitron text-sm uppercase tracking-wider hover:bg-magenta-400 hover:text-white transition-colors"
            >
              <MessageSquare className="w-4 h-4" /> Mes tickets
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HelpCenter;
